import { useEffect, useState } from "react";
import { AiIcon } from "./AiIcon";
import { AiButton } from "./AiButton";
import { StatusPill } from "@/components/patterns";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { useAiFeature } from "@/lib/ai";
import { timeAgo } from "@/pages/deals/record/DealRecordHeader";

export type DealHealthLevel = "GOOD" | "WATCH" | "AT_RISK" | "UNKNOWN";
interface Health {
  health: DealHealthLevel;
  previous_health: DealHealthLevel | null;
  summary: string | null;
  reasons: string[];
  next_action: string | null;
  assessed_at: string;
  source: "NIGHTLY" | "ON_DEMAND";
}

export const HEALTH_LABEL: Record<DealHealthLevel, string> = { GOOD: "Good", WATCH: "Watch", AT_RISK: "At risk", UNKNOWN: "Not sure" };
export const HEALTH_TONE = { GOOD: "success", WATCH: "warning", AT_RISK: "danger", UNKNOWN: "neutral" } as const;

/**
 * The AI's read of how a deal is going (checked every night when the deal changed): Good / Watch / At risk, why, and
 * the next best action. "Check now" asks again straight away. It only advises; nothing on the deal changes.
 */
export function DealHealthCard({ dealId, isClosed }: { dealId: string; isClosed: boolean }) {
  const available = useAiFeature("DEAL_HEALTH");
  const { error } = useToast();
  const [health, setHealth] = useState<Health | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!available) return;
    api
      .get(`/api/Ai/DealHealth/${dealId}`)
      .then((r) => setHealth(unwrapObject<Health | null>(r, null)))
      .catch(() => undefined);
  }, [available, dealId]);

  if (!available || isClosed) return null;

  const check = async () => {
    setBusy(true);
    try {
      setHealth(unwrapObject<Health>(await api.post(`/api/Ai/DealHealth/${dealId}/Refresh`)));
    } catch (e: any) {
      error("Couldn't check the deal", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-2.5 rounded-lg border border-primary/25 bg-accent/40 px-3.5 py-3" aria-label="AI deal health">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-accent-foreground">
          <AiIcon className="size-4" busy={busy} /> Deal health
        </h2>
        {health && <StatusPill tone={HEALTH_TONE[health.health]}>{HEALTH_LABEL[health.health]}</StatusPill>}
      </div>
      {health ? (
        <>
          {health.summary && <p className="text-[13px]">{health.summary}</p>}
          {health.reasons?.length > 0 && (
            <ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-muted-foreground">
              {health.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
          {health.next_action && (
            <p className="rounded-md bg-background/70 px-2.5 py-1.5 text-[12.5px]">
              <span className="font-semibold">Next: </span>
              {health.next_action}
            </p>
          )}
          <div className="flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
            <span>Checked {timeAgo(health.assessed_at)}</span>
            <button type="button" className="font-medium text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={check}>
              {busy ? "Checking…" : "Check now"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Not checked yet. Deals are checked every night; you can check this one now.</p>
          <AiButton busy={busy} onClick={check} title="Ask the AI how this deal is going and what to do next">
            Check deal health
          </AiButton>
        </>
      )}
    </section>
  );
}
